import { ChangeEvent, forwardRef, useImperativeHandle, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { PatternFormat } from "react-number-format";
import TextareaAutosize from "react-textarea-autosize";
import { ReactComponent as Close } from "../../assets/icons/close_rounded.svg";
import { ReactComponent as ReadPassword } from "../../assets/icons/read_password_outline.svg";
import { ReactComponent as UnreadPassword } from "../../assets/icons/unreadPassword.svg";

type Props = {
    format?: string;
    value: string;
    textMaxLength?: number;
    onClickClearButton: () => void;
    error?: boolean | undefined;
    className?: string;
    wrapperClassName?: string;
    [key: string]: any;
};
const InputField = forwardRef<HTMLInputElement, Props>(
    ({ value, onClickClearButton, error, textMaxLength, className, wrapperClassName, ...rest }, ref) => {
        const wrapperStyle = twMerge(
            `flex justify-between items-center pr-[13px] rounded-xl border overflow-hidden [&:has(input:focus)]:border-[#4B632B] flex-1 ${error && "[&:has(input)]:!border-[#DC2626]"}`,
            wrapperClassName,
        );
        const style = twMerge("peer flex-1 p-3 bg-white placeholder:text-[#ddd] text-lg focus:outline-none focus:ring-0", className);

        const inputRef = useRef<HTMLInputElement | null>(null);

        // 부모 컴포넌트에서 ref를 통해 inputRef에 접근할 수 있도록 설정
        useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

        return (
            <div className={wrapperStyle}>
                <input ref={inputRef} value={value} className={style} {...rest} maxLength={textMaxLength ?? undefined} />
                {value?.length > 0 && textMaxLength && (
                    <span className="opacity-0 peer-focus:opacity-[1] text-[14px] text-[#484964] mx-[5px]">
                        {value?.length}/{textMaxLength}
                    </span>
                )}
                {value?.length > 0 && (
                    <button
                        type="button"
                        title="내용 지우기"
                        className="opacity-0 peer-focus:opacity-[1]"
                        tabIndex={-1}
                        onClick={() => {
                            onClickClearButton();
                            inputRef.current?.focus();
                        }}
                    >
                        <Close />
                    </button>
                )}
            </div>
        );
    },
);
export default InputField;

export function MobileInputField({ format, value, onClickClearButton, error, className, ...rest }: Props) {
    const ref = useRef<HTMLInputElement | null>(null);
    const style = twMerge("peer flex-1 p-3 bg-white placeholder:text-[#ddd] text-lg focus:outline-none focus:ring-0", className);

    return (
        <div
            className={`flex justify-between items-center pr-[13px] rounded-xl border overflow-hidden [&:has(input:focus)]:border-[#4B632B] ${error && "[&:has(input)]:!border-[#DC2626]"}`}
        >
            <PatternFormat getInputRef={ref} format={format ?? "###-####-####"} value={value} className={style} {...rest} />
            {value.length > 0 && (
                <button
                    type="button"
                    title="내용 지우기"
                    className="opacity-0 peer-focus:opacity-[1]"
                    tabIndex={-1}
                    onClick={() => {
                        onClickClearButton();
                        ref.current?.focus();
                    }}
                >
                    <Close />
                </button>
            )}
        </div>
    );
}

export function PasswordInputField({ value, onClickClearButton, error, className, ...rest }: Props) {
    const [passwordVisible, setPasswordVisible] = useState(false);
    const ref = useRef<HTMLInputElement | null>(null);
    // const style = twMerge("peer flex-1 p-3 bg-white placeholder:text-[#ddd] text-lg focus:outline-none focus:ring-0", className);

    const togglePasswordVisibility = () => {
        setPasswordVisible(prev => !prev);
    };

    return (
        <div
            className={`flex items-center gap-3 p-3 rounded-xl border bg-white [&:has(input:focus)]:border-[#4B632B] ${error && "[&:has(input)]:!border-[red]"}`}
        >
            <input
                type={passwordVisible ? "text" : "password"}
                ref={ref}
                value={value}
                className="peer flex-1 placeholder:text-[#ddd] text-lg focus:outline-none focus:ring-0"
                {...rest}
            />
            {value.length > 0 && (
                <button
                    type="button"
                    title="내용 지우기"
                    className="opacity-0 peer-focus:opacity-[1]"
                    tabIndex={-1}
                    onClick={() => {
                        onClickClearButton();
                        ref.current?.focus();
                    }}
                >
                    <Close />
                </button>
            )}
            {passwordVisible ? (
                <ReadPassword className="cursor-pointer ml-auto" onClick={togglePasswordVisibility} />
            ) : (
                <UnreadPassword className="cursor-pointer ml-auto" onClick={togglePasswordVisibility} />
            )}
        </div>
    );
}

export function TextareaField({
    value,
    name,
    onChange,
    placeholder,
    textMaxLength,
}: {
    value: string;
    name?: string;
    onChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
    placeholder?: string;
    textMaxLength?: number;
}) {
    return (
        <div className="relative">
            <TextareaAutosize
                name={name}
                value={value}
                placeholder={placeholder}
                onChange={onChange}
                maxLength={textMaxLength}
                className="w-full whitespace-pre-wrap resize-none peer border border-[#ddd] rounded-[12px] p-[12px] min-h-[132px] focus:border-[#4B632B] focus:outline-none"
            />

            {value && textMaxLength && (
                <p className="opacity-0 peer-focus:opacity-[1] text-[14px] text-[#484964] text-right">
                    {value?.length}/{textMaxLength}
                </p>
            )}
        </div>
    );
}
